import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';
import { fetchCustomerEntriesBalance, withCommas } from '@theme/clean-sweeps';

const ACCOUNT_LOGIN_BRAND_SHEET = new CSSStyleSheet();
ACCOUNT_LOGIN_BRAND_SHEET.replaceSync(`
  .login-form__email-submit:disabled {
    background-color: #1c2e60 !important;
    color: #f6f0e3 !important;
    opacity: 1;
    border-radius: var(--shopify-account-radius-button) !important;
  }

  .login-form__email-submit,
  .login-form__social-login-button {
    border-radius: var(--shopify-account-radius-button) !important;
  }

  .login-form__email-submit:disabled .login-form__email-submit-icon {
    color: #f6f0e3 !important;
  }

  .login-form__email-label {
    font-family: Roboto, "Roboto Condensed", sans-serif !important;
    font-size: 16px !important;
    font-weight: 400 !important;
    color: #7a8799 !important;
  }

  .login-form__email-submit:hover:not(:disabled),
  .button.primary:hover:not(:disabled) {
    background-color: #191f38 !important;
    color: #f6f0e3 !important;
  }

  .login-form__email-input {
    border-color: #7a8799 !important;
  }

  .login-form__email-input:focus-visible {
    border-color: #7a8799 !important;
  }

  .login-form__divider-line {
    background-color: #7a8799 !important;
  }

  .login-form__social-login-button {
    border-color: #7a8799 !important;
  }
`);

const ACCOUNT_SHEET_BRAND_CSS = `
  .account__heading {
    font-family: Lora, "Lora Condensed", serif !important;
    font-size: 20px !important;
    font-weight: 500 !important;
    color: #25335e !important;
  }

  .account__header,
  .account__header-content {
    margin-block-end: 0 !important;
    padding-block-end: 0 !important;
  }

  .account__entries-balance {
    margin: 8px 0 0 !important;
    padding: 0 !important;
    font-family: "Roboto Condensed", Roboto, sans-serif !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    line-height: 1.3 !important;
    color: #1c2e60 !important;
  }

  .account__entries-balance strong {
    font-weight: 600 !important;
  }

  .account__menu-region {
    margin-block-start: 10px !important;
    margin-block-end: 0 !important;
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
    gap: 0 !important;
  }

  .account__menu-region > hr,
  .account__menu-region > .divider,
  .account__shortcuts + hr,
  .account__header + hr,
  .account__header-content + hr {
    display: none !important;
  }

  .account__menu-items,
  .account__menu-items .stack-inline,
  .account__shortcuts {
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 8px !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
  }

  .account__item-wrapper {
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  .account__menu-items .button.outline,
  .account__shortcuts .account__item,
  .account__menu-region .account__item {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 10px !important;
    width: 100% !important;
    min-height: 44px !important;
    margin: 0 !important;
    padding: 12px 16px !important;
    border: 1px solid #7a8799 !important;
    border-radius: var(--shopify-account-radius-button) !important;
    background: #1c2e60 !important;
    box-shadow: none !important;
    color: #f6f0e3 !important;
    font-family: "Roboto Condensed", Roboto, sans-serif !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    line-height: 1.2 !important;
    letter-spacing: 0.02em !important;
    text-align: center !important;
    text-decoration: none !important;
    text-transform: uppercase !important;
    transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease !important;
  }

  .account__menu-items .button.outline:hover,
  .account__menu-items .button.outline:focus,
  .account__menu-items .button.outline:focus-visible,
  .account__shortcuts .account__item:hover,
  .account__shortcuts .account__item:focus,
  .account__shortcuts .account__item:focus-visible,
  .account__shortcuts .account__item[aria-current='page'],
  .account__shortcuts .account__item[aria-current='true'],
  .account__menu-region .account__item:hover,
  .account__menu-region .account__item:focus,
  .account__menu-region .account__item:focus-visible {
    background: #191f38 !important;
    border-color: #7a8799 !important;
    color: #f6f0e3 !important;
    outline: none !important;
    box-shadow: none !important;
  }

  .account__menu-items .button.outline .icon,
  .account__menu-items .button.outline svg,
  .account__shortcuts .account__item .icon,
  .account__shortcuts .account__item svg,
  .account__menu-region .button.outline .icon,
  .account__menu-region .button.outline svg,
  .account__menu-region .account__item .icon,
  .account__menu-region .account__item svg {
    display: block !important;
    position: static !important;
    inset: auto !important;
    left: auto !important;
    right: auto !important;
    top: auto !important;
    bottom: auto !important;
    flex: 0 0 auto !important;
    flex-shrink: 0 !important;
    width: 16px !important;
    height: 16px !important;
    margin: 0 !important;
    color: #f6f0e3 !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  /* Fill-based Shopify icons (Orders / tracking) */
  .account__menu-region svg.icon.tracking,
  .account__menu-region svg.icon.tracking path {
    fill: currentColor !important;
    stroke: none !important;
  }

  /* Stroke-based Shopify icons (Profile / Subscriptions) */
  .account__menu-region svg.icon.profile-circle,
  .account__menu-region svg.icon.profile-circle path,
  .account__menu-region svg.icon.subscription,
  .account__menu-region svg.icon.subscription path {
    fill: none !important;
    stroke: currentColor !important;
  }

  .account__menu-region [data-bdgc-account-cluster] {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 10px !important;
    max-width: 100% !important;
  }

  .account__item-text {
    position: static !important;
    display: inline !important;
    flex: 0 0 auto !important;
    width: auto !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
    color: inherit !important;
    font: inherit !important;
    text-align: center !important;
    text-transform: inherit !important;
  }

  .account__item-text::after {
    content: none !important;
    display: none !important;
  }

  /* Keep icon + label as one centered cluster (no trailing chevron space) */
  .account__menu-region .account__item > .icon:last-child:not(.tracking):not(.profile-circle):not(.subscription),
  .account__menu-region .account__item > svg:last-child:not(.tracking):not(.profile-circle):not(.subscription) {
    display: none !important;
  }

  .account__close,
  .account__close:hover,
  .account__close:focus,
  .account__close:active {
    background-color: transparent !important;
    background: transparent !important;
    box-shadow: none !important;
    filter: none !important;
    opacity: 1 !important;
  }

  .account__close::before,
  .account__close:hover::before {
    background-color: transparent !important;
    opacity: 0 !important;
  }

  .button.outline {
    background-color: #1c2e60 !important;
    border-color: #7a8799 !important;
    color: #f6f0e3 !important;
    font-family: "Roboto Condensed", Roboto, sans-serif !important;
    font-size: 14px !important;
    border-radius: var(--shopify-account-radius-button) !important;
  }

  .button.outline:hover,
  .button.outline:focus-visible {
    background-color: #191f38 !important;
    border-color: #7a8799 !important;
    color: #f6f0e3 !important;
  }

  .button.outline .icon,
  .button.outline svg {
    color: #f6f0e3 !important;
  }

  .button.primary,
  .button {
    font-family: "Roboto Condensed", Roboto, sans-serif !important;
    font-size: 14px !important;
    border-radius: var(--shopify-account-radius-button) !important;
  }
`;

/**
 * Always upsert brand CSS into the shadow root so hot reloads replace stale rules.
 * @param {ShadowRoot | null | undefined} root
 */
function applyAccountBrandStyles(root) {
  if (!root) return;

  let style = root.querySelector('style[data-bdgc-account-brand]');
  if (!style) {
    style = document.createElement('style');
    style.setAttribute('data-bdgc-account-brand', '');
    root.appendChild(style);
  }

  if (style.textContent !== ACCOUNT_SHEET_BRAND_CSS) {
    style.textContent = ACCOUNT_SHEET_BRAND_CSS;
  }
}

/** Shopify account menu icons (same markup as account__menu-region quick links). */
const ACCOUNT_MENU_ICONS = {
  orders: `<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" class="icon tracking" viewBox="0 0 16 16"><path fill="currentColor" d="M4.667 2.32v-.75a.75.75 0 0 0-.624.333zm-2 10.666h-.75c0 .414.336.75.75.75zm8.666-10.667.624-.416a.75.75 0 0 0-.624-.334zm2 10.667v.75a.75.75 0 0 0 .75-.75zM2.667 5.319l-.624-.416a.75.75 0 0 0-.126.416zm10.666 0h.75a.75.75 0 0 0-.126-.416zm-8 4.25a.75.75 0 0 0 0 1.5v-1.5M8 11.07a.75.75 0 0 0 0-1.5v1.5m-5.333 1.917v.75h10.666v-1.5H2.667zm2-10.667-.624-.416-2 3 .624.416.624.416 2-3zm6.666 0-.624.416 2 3 .624-.416.624-.416-2-3zm2 3h-.75v7.667h1.5V5.319zm-8.666-3v.75H8v-1.5H4.667zM8 2.32v.75h3.333v-1.5H8zm-5.333 3v.75H8v-1.5H2.667zM8 5.32v.75h5.333v-1.5H8zm0 0h.75v-3h-1.5v3zm-5.333 0h-.75v7.667h1.5V5.319zm2.666 5v.75H8v-1.5H5.333z"></path></svg>`,
  profile: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16" aria-hidden="true" class="icon profile-circle"><path stroke="currentColor" stroke-linejoin="round" stroke-width="1.333" d="M4.077 12.54c.887-1.144 2.257-1.873 3.923-1.873s3.036.73 3.923 1.873m-7.846 0A5.98 5.98 0 0 0 8 14c1.5 0 2.87-.55 3.923-1.46m-7.846 0a6 6 0 1 1 7.845 0M10 6.667a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z"></path></svg>`,
  subscriptions: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16" aria-hidden="true" class="icon subscription"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.333" d="M2.667 8A5.333 5.333 0 0 1 12.4 4.8M13.333 8A5.333 5.333 0 0 1 3.6 11.2M12.4 4.8 10.667 3.333M12.4 4.8l-.534 2.133M3.6 11.2l1.733 1.467M3.6 11.2l.534-2.133"/></svg>`,
};

/**
 * @param {string} href
 * @param {string} label
 * @returns {keyof typeof ACCOUNT_MENU_ICONS | null}
 */
function resolveAccountMenuIconKey(href, label) {
  const text = label.toLowerCase();
  const path = href.toLowerCase();

  if (/subscription/.test(text) || /subscription/.test(path)) return 'subscriptions';
  if (/order/.test(text) || /\/orders?\b|tracking/.test(path)) return 'orders';
  if (/profile|address/.test(text) || /\/profile\b|\/addresses\b/.test(path)) return 'profile';
  return null;
}

/**
 * The 3-link signed-in shortcuts menu often renders without SVGs.
 * Inject the same icons Shopify uses on quick-link outline buttons,
 * and wrap icon + label so they center as one group.
 * @param {ShadowRoot | null | undefined} root
 */
function injectAccountMenuIcons(root) {
  if (!root) return;

  const links = root.querySelectorAll(
    '.account__menu-region .account__item, .account__shortcuts .account__item, .account__menu-region .button.outline',
  );

  for (const link of links) {
    if (!(link instanceof HTMLElement)) continue;
    if (link.querySelector('[data-bdgc-account-cluster]')) continue;

    const label = link.textContent?.trim() || '';
    const href = link.getAttribute('href') || '';
    const key = resolveAccountMenuIconKey(href, label);

    const cluster = document.createElement('span');
    cluster.setAttribute('data-bdgc-account-cluster', key || '');

    while (link.firstChild) {
      cluster.appendChild(link.firstChild);
    }

    if (key && !cluster.querySelector('svg.icon, svg[aria-hidden="true"]')) {
      const wrap = document.createElement('span');
      wrap.innerHTML = ACCOUNT_MENU_ICONS[key];
      const svg = wrap.querySelector('svg');
      if (svg) cluster.insertBefore(svg, cluster.firstChild);
    }

    link.appendChild(cluster);
  }
}

/**
 * @param {Element | null | undefined} login
 */
function patchAccountLoginForm(login) {
  if (!(login instanceof HTMLElement) || !login.shadowRoot || login.dataset.brandPatched === 'true') return;

  login.shadowRoot.adoptedStyleSheets = [...login.shadowRoot.adoptedStyleSheets, ACCOUNT_LOGIN_BRAND_SHEET];
  login.dataset.brandPatched = 'true';
}

/**
 * Inject Entries balance into the signed-in account sheet (from Clean Sweeps).
 * @param {HTMLElement} account
 */
function injectAccountEntriesBalance(account) {
  const root = account.shadowRoot;
  if (!root) return;

  const source = account.closest('[data-entries-label]') || account.closest('.account-button') || account;
  const label = source.dataset.entriesLabel || document.body?.dataset?.entriesLabel || 'Entries balance';

  const heading = root.querySelector('.account__heading');
  const headingText = heading?.textContent?.trim() || '';
  const isSignInSheet = /sign in|create account|log in/i.test(headingText);
  const isSignedInSheet = Boolean(heading) && !isSignInSheet && Boolean(root.querySelector('.account__menu-region'));

  if (!isSignedInSheet) {
    root.querySelector('.account__entries-balance')?.remove();
    return;
  }

  const identity = resolveCustomerIdentity(account, root, source);
  if (!identity.customerId && !identity.email) {
    refreshCustomerIdentityFromSection(account);
    return;
  }

  const fetchKey = `${identity.customerId}|${identity.email}|${identity.sweepstakeId}`;
  if (account.dataset.entriesFetchKey === fetchKey && account.dataset.entriesBalanceReady === 'true') {
    paintAccountEntriesBalance(root, label, account.dataset.entriesBalanceValue || '0');
    return;
  }

  if (account.dataset.entriesFetchKey === fetchKey && account.dataset.entriesFetching === 'true') {
    return;
  }

  account.dataset.entriesFetchKey = fetchKey;
  account.dataset.entriesFetching = 'true';
  paintAccountEntriesBalance(root, label, '…');

  fetchCustomerEntriesBalance({
    platformUserId: identity.customerId,
    email: identity.email,
    sweepstakeId: identity.sweepstakeId,
  })
    .then((balance) => {
      if (account.dataset.entriesFetchKey !== fetchKey) return;
      const value = balance == null ? 0 : balance;
      account.dataset.entriesBalanceValue = String(value);
      account.dataset.entriesBalanceReady = 'true';
      try {
        localStorage.setItem('bdgc_entries_balance', String(value));
        localStorage.setItem('bdgc_entries_label', label);
      } catch {
        // Ignore private-mode storage failures.
      }
      paintAccountEntriesBalance(root, label, String(value));
    })
    .catch(() => {
      if (account.dataset.entriesFetchKey !== fetchKey) return;
      let cached = '';
      try {
        cached = localStorage.getItem('bdgc_entries_balance') || '';
      } catch {
        cached = '';
      }
      if (cached !== '') paintAccountEntriesBalance(root, label, cached);
    })
    .finally(() => {
      if (account.dataset.entriesFetchKey === fetchKey) {
        delete account.dataset.entriesFetching;
      }
    });
}

/**
 * @param {HTMLElement} account
 * @param {ShadowRoot} root
 * @param {HTMLElement} source
 * @returns {{ customerId: string, email: string, sweepstakeId: string }}
 */
function resolveCustomerIdentity(account, root, source) {
  const customerId =
    source.dataset.customerId ||
    document.body?.dataset?.customerId ||
    account.dataset.customerId ||
    '';
  let email =
    source.dataset.customerEmail ||
    document.body?.dataset?.customerEmail ||
    account.dataset.customerEmail ||
    '';

  if (!email) {
    const emailNode = [...root.querySelectorAll('p, span, div')].find((node) => {
      const text = node.textContent?.trim() || '';
      return text.includes('@') && !node.classList.contains('account__entries-balance');
    });
    email = emailNode?.textContent?.trim() || '';
  }

  const sweepstakeId =
    source.dataset.sweepstakeId ||
    document.body?.dataset?.sweepstakeId ||
    account.dataset.sweepstakeId ||
    '';

  return {
    customerId: String(customerId || '').replace(/\D/g, ''),
    email: String(email || '').trim().toLowerCase(),
    sweepstakeId: String(sweepstakeId || '').replace(/\D/g, ''),
  };
}

/**
 * @param {ShadowRoot} root
 * @param {string} label
 * @param {string} balanceRaw
 */
function paintAccountEntriesBalance(root, label, balanceRaw) {
  const parsed = Number.parseInt(String(balanceRaw).replace(/[^\d-]/g, ''), 10);
  const displayBalance = balanceRaw === '…' ? '…' : withCommas(Number.isFinite(parsed) ? parsed : 0);
  const nextText = `${label}: ${displayBalance}`;

  let el = root.querySelector('.account__entries-balance');
  if (el && el.dataset.renderedText === nextText) return;

  if (!el) {
    el = document.createElement('p');
    el.className = 'account__entries-balance';

    const heading = root.querySelector('.account__heading');
    const headerContent = root.querySelector('.account__header-content');
    const menuRegion = root.querySelector('.account__menu-region');

    if (headerContent) {
      headerContent.appendChild(el);
    } else if (menuRegion?.parentElement) {
      menuRegion.parentElement.insertBefore(el, menuRegion);
    } else if (heading) {
      heading.insertAdjacentElement('afterend', el);
    } else {
      return;
    }
  }

  el.replaceChildren();
  el.append(document.createTextNode(`${label}: `));
  const valueNode = document.createElement('strong');
  valueNode.textContent = String(displayBalance);
  el.append(valueNode);
  el.dataset.renderedText = nextText;
}

/**
 * Re-fetch customer id/email after New Customer Accounts bridges the storefront session.
 * @param {HTMLElement} account
 */
let entriesBalanceRefreshPromise = null;
function refreshCustomerIdentityFromSection(account) {
  if (entriesBalanceRefreshPromise) return entriesBalanceRefreshPromise;

  const rootPath = window.Shopify?.routes?.root || '/';
  entriesBalanceRefreshPromise = fetch(`${rootPath}?sections=entries-balance-data`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  })
    .then((response) => (response.ok ? response.json() : null))
    .then((payload) => {
      const html = payload?.['entries-balance-data'];
      if (!html) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const node = doc.querySelector('#entries-balance-data');
      if (!node || node.dataset.hasCustomer !== 'true') return;

      const wrapper = account.closest('.account-button') || account;
      if (node.dataset.customerId) wrapper.dataset.customerId = node.dataset.customerId;
      if (node.dataset.customerEmail) wrapper.dataset.customerEmail = node.dataset.customerEmail;
      if (node.dataset.sweepstakeId) wrapper.dataset.sweepstakeId = node.dataset.sweepstakeId;
      if (node.dataset.label) wrapper.dataset.entriesLabel = node.dataset.label;
      if (node.dataset.shopGid) wrapper.dataset.shopGid = node.dataset.shopGid;

      delete account.dataset.entriesFetchKey;
      delete account.dataset.entriesBalanceReady;
      injectAccountEntriesBalance(account);
    })
    .catch(() => {
      // Silent — balance stays hidden if session is not bridged.
    })
    .finally(() => {
      entriesBalanceRefreshPromise = null;
    });

  return entriesBalanceRefreshPromise;
}

/**
 * @param {HTMLElement} account
 */
function syncAccountDialogTop(account) {
  if (window.matchMedia('(max-width: 749px)').matches) {
    account.style.removeProperty('--shopify-account-dialog-position-top');
    return;
  }

  const header = document.querySelector('#header-component');
  if (!header) return;

  let top = Math.ceil(header.getBoundingClientRect().bottom) + 1;
  if (top < 1) {
    top = Math.ceil(header.offsetHeight || 0) + 1;
  }
  account.style.setProperty('--shopify-account-dialog-position-top', `${top}px`);
}

/**
 * Walk nested shadow roots so menu shortcuts get brand styles too.
 * @param {ParentNode | null | undefined} node
 */
function patchAccountNestedRoots(node) {
  if (!node) return;

  const elements = node.querySelectorAll?.('*') || [];
  for (const el of elements) {
    if (!(el instanceof HTMLElement) || !el.shadowRoot) continue;
    applyAccountBrandStyles(el.shadowRoot);
    patchAccountNestedRoots(el.shadowRoot);
  }
}

/**
 * @param {Element | null | undefined} account
 */
function patchShopifyAccount(account) {
  if (!(account instanceof HTMLElement) || !account.shadowRoot) return;

  applyAccountBrandStyles(account.shadowRoot);
  injectAccountMenuIcons(account.shadowRoot);

  if (account.dataset.brandPatched === 'true') {
    patchAccountLoginForm(account.shadowRoot.querySelector('shopify-login-form'));
    patchAccountNestedRoots(account.shadowRoot);
    injectAccountEntriesBalance(account);
    return;
  }

  account.dataset.brandPatched = 'true';

  patchAccountLoginForm(account.shadowRoot.querySelector('shopify-login-form'));
  patchAccountNestedRoots(account.shadowRoot);
  injectAccountEntriesBalance(account);

  const observer = new MutationObserver(() => {
    applyAccountBrandStyles(account.shadowRoot);
    injectAccountMenuIcons(account.shadowRoot);
    patchAccountLoginForm(account.shadowRoot?.querySelector('shopify-login-form'));
    patchAccountNestedRoots(account.shadowRoot);
    injectAccountEntriesBalance(account);
  });
  observer.observe(account.shadowRoot, { childList: true, subtree: true });

  const syncTop = () => {
    syncAccountDialogTop(account);
    injectAccountMenuIcons(account.shadowRoot);
    injectAccountEntriesBalance(account);
  };
  account.addEventListener('open', syncTop);
  account.addEventListener('click', syncTop);

  const dialog = account.shadowRoot.querySelector('dialog');
  if (dialog) {
    const dialogObserver = new MutationObserver(() => {
      if (dialog.open) syncTop();
    });
    dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  syncTop();
  window.addEventListener('resize', syncTop, { passive: true });
  window.addEventListener('scroll', syncTop, { passive: true });
  const pageWrapper = document.querySelector('.page-wrapper');
  if (pageWrapper instanceof HTMLElement) {
    pageWrapper.addEventListener('scroll', syncTop, { passive: true });
  }
}

function initShopifyAccountBranding() {
  customElements.whenDefined('shopify-account').then(() => {
    document.querySelectorAll('shopify-account').forEach(patchShopifyAccount);
  });
}

initShopifyAccountBranding();

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
    this.querySelectorAll('shopify-account').forEach(patchShopifyAccount);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
  }

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
